import type { TemplateBlock } from '@rentflowdev/api'

interface Props {
	blocks: TemplateBlock[]
	filledData?: Record<string, unknown>
}

function deepGet(obj: Record<string, unknown>, path: string): unknown {
	return path.split('.').reduce<unknown>((acc, key) => {
		if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key]
		return undefined
	}, obj)
}

function resolveField(block: TemplateBlock, filledData: Record<string, unknown>): string {
	if (block.fieldKey) {
		const v = deepGet(filledData, block.fieldKey)
		if (v !== null && v !== undefined && v !== '') return String(v)
	}
	return ''
}

// ── Token types ────────────────────────────────────────────────────────────────
// We stream all non-SECTION blocks into inline tokens.
// '\n' in TEXT content creates explicit paragraph breaks.
type Token =
	| { type: 'text'; content: string }
	| { type: 'field'; block: TemplateBlock; value: string; empty: boolean }
	| { type: 'break' }

function blocksToTokens(blocks: TemplateBlock[], filledData: Record<string, unknown>): Token[] {
	const tokens: Token[] = []

	for (const block of blocks) {
		if (block.type === 'SECTION' || block.type === 'SUBSECTION') continue

		if (block.type === 'FIELD') {
			const value = resolveField(block, filledData)
			tokens.push({ type: 'field', block, value, empty: !value })
			continue
		}

		// TEXT — split on \n to create paragraph breaks
		const lines = (block.content ?? '').split('\n')
		for (let i = 0; i < lines.length; i++) {
			if (i > 0) tokens.push({ type: 'break' })
			if (lines[i]) tokens.push({ type: 'text', content: lines[i] })
		}
	}

	return tokens
}

// Renders a flat token list as <p> elements (split at 'break' tokens).
function TokenFlow({ tokens }: { tokens: Token[] }) {
	const paragraphs: Token[][] = []
	let current: Token[] = []

	for (const token of tokens) {
		if (token.type === 'break') {
			if (current.length) paragraphs.push(current)
			current = []
		} else {
			current.push(token)
		}
	}
	if (current.length) paragraphs.push(current)

	return (
		<>
			{paragraphs.map((para, pi) => (
				<p key={pi} className="mb-2 last:mb-0">
					{para.map((token, ti) => {
						if (token.type === 'text') {
							return <span key={ti}>{token.content}</span>
						}
						if (token.type === 'field') {
							if (!token.empty) {
								return (
									<span
										key={ti}
										className="font-medium underline decoration-dotted decoration-1 underline-offset-2"
									>
										{' '}{token.value}{' '}
									</span>
								)
							}
							return (
								<span
									key={ti}
									className="inline-block min-w-[80px] border-b border-dashed border-(--color-muted-foreground)/50 mx-0.5 text-(--color-muted-foreground)/60 text-xs italic align-baseline px-1"
								>
									{token.block.content}
								</span>
							)
						}
						return null
					})}
				</p>
			))}
		</>
	)
}

export function DocumentBlockPreview({ blocks, filledData = {} }: Props) {
	const sorted = [...blocks].sort((a, b) => a.blockOrder - b.blockOrder)

	// Split blocks into groups separated by SECTION markers
	type Group = { heading: string | null; subheading: string | null; items: TemplateBlock[] }
	const groups: Group[] = []
	let current: Group = { heading: null, subheading: null, items: [] }

	for (const block of sorted) {
		if (block.type === 'SECTION') {
			if (current.items.length || current.heading) groups.push(current)
			current = { heading: block.content, subheading: null, items: [] }
		} else if (block.type === 'SUBSECTION') {
			if (current.items.length) {
				groups.push(current)
				current = { heading: null, subheading: block.content, items: [] }
			} else {
				current.subheading = block.content
			}
		} else {
			current.items.push(block)
		}
	}
	groups.push(current)

	return (
		<div
			className="mx-auto max-w-[720px] px-16 py-12 text-[13px] leading-[1.65] text-(--color-foreground)"
			style={{ fontFamily: '"Times New Roman", Times, serif' }}
		>
			{groups.map((group, gi) => {
				const tokens = blocksToTokens(group.items, filledData)

				return (
					<div key={gi} className={gi === 0 ? '' : 'mt-5'}>
						{group.heading && (
							<h2 className="mb-3 mt-6 text-sm font-bold uppercase tracking-wide first:mt-0">
								{group.heading}
							</h2>
						)}
						{group.subheading && (
							<h3 className="mb-2 mt-4 text-sm font-semibold">
								{group.subheading}
							</h3>
						)}
						{tokens.length > 0 && (
							<div className="text-justify">
								<TokenFlow tokens={tokens} />
							</div>
						)}
					</div>
				)
			})}
		</div>
	)
}
